/**
 * App-wide constants and the quota-gate CTA hierarchy.
 *
 * The CTA hierarchy is data, not JSX, so it can flip when payment ships
 * (pre-payment: Contact admin + BYO key; post-payment: Upgrade/Pay primary)
 * without touching the gate component.
 */

export const ADMIN_EMAIL = 'admin@getproofofwork.com'

/**
 * Single builder for every admin mailto in the app. Legal pages call it with no
 * args (plain address); the quota / Fix-Key gates pass a subject + body so the
 * user's message arrives with context. Keeping construction here is the uniform
 * structure — no page hand-rolls its own `mailto:` string.
 */
export function contactMailto(subject?: string, body?: string): string {
  const params = new URLSearchParams()
  if (subject) params.set('subject', subject)
  if (body) params.set('body', body)
  const qs = params.toString()
  return `mailto:${ADMIN_EMAIL}${qs ? `?${qs}` : ''}`
}

/** Back-compat plain mailto (no prefill) — identical to the pre-builder value. */
export const CONTACT_MAILTO = contactMailto()

/** Prefill shared by the quota wall and the invalid-key gate. */
export const SUPPORT_SUBJECT = 'Proof of Work — usage limit / API key help'
export const SUPPORT_BODY =
  "Hi,\n\nI've hit a limit using Proof of Work and need help to keep going.\n\n" +
  'My account email: \n\nThanks,'

/** Deep-link to the working BYO API-key tab in Settings. */
export const BYO_KEY_SETTINGS_PATH = '/settings?tab=apikey'

export type QuotaCtaKind = 'contact_admin' | 'use_byo_key' | 'upgrade'

export interface QuotaCta {
  kind: QuotaCtaKind
  label: string
  /** Internal route (react-router) — mutually exclusive with href. */
  to?: string
  /** External/mailto href — mutually exclusive with to. */
  href?: string
  /** Visual weight; the gate renders `primary` CTAs more prominently. */
  emphasis: 'primary' | 'secondary'
}

/**
 * Pre-payment ordering. Both CTAs are co-prominent because the operational
 * response to a hit cap is "raise the cap manually and/or steer to BYO."
 * When payment ships, add an `upgrade` primary CTA here and demote these.
 */
export const QUOTA_CTAS: QuotaCta[] = [
  {
    kind: 'contact_admin',
    label: 'Contact admin',
    href: contactMailto(SUPPORT_SUBJECT, SUPPORT_BODY),
    emphasis: 'primary',
  },
  {
    kind: 'use_byo_key',
    label: 'Use your own API key',
    to: BYO_KEY_SETTINGS_PATH,
    emphasis: 'primary',
  },
]

/**
 * Invalid/revoked BYO key. The user can self-fix, so "update key" is the primary
 * action and "contact admin" is the secondary fallback. `use_byo_key.to` is the
 * default destination (Settings); a caller in the onboarding flow may override it.
 */
export const BYO_KEY_INVALID_CTAS: QuotaCta[] = [
  {
    kind: 'use_byo_key',
    label: 'Update your API key',
    to: BYO_KEY_SETTINGS_PATH,
    emphasis: 'primary',
  },
  {
    kind: 'contact_admin',
    label: 'Contact admin',
    href: contactMailto(SUPPORT_SUBJECT, SUPPORT_BODY),
    emphasis: 'secondary',
  },
]
